import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import { ButtonGroup, Button, DropdownButton, Dropdown, Col, Row, Modal } from 'react-bootstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Annotations from '../../../api/Annotations/Annotations';
import * as projectOps from '../../../api/Projects/project-operations';
import Projects from '../../../api/Projects/Projects';
import * as videoOps from '../../../api/Videos/video-operations';
import * as videoCalculate from '../../../api/Videos/videos-calculations';
import Videos from '../../../api/Videos/Videos';

import {
  exportTextToLocalFile,
  importFromAnnotationsFile
} from '../../../modules/export-import-files';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import ButtonImport from '../../atoms/ButtonImport';
import ConfirmationModalImport from '../../atoms/ConfirmationModalImport/ConfirmationModalImport';
import CopyURLIcon from '../../atoms/CopyURLIcon';
import PageHeader from '../../atoms/PageHeader';
import ShareIcon from '../../atoms/ShareIcon';
import Loading from '../../components/Loading';
import SEO from '../../components/SEO';
import VideoVisualizerContainer from '../../containers/VideoVisualizerContainer';
import NotFound from '../NotFound';

// eslint-disable-next-line no-unused-vars
const handleExport = async (isSyncGPS, video, handleCloseModalExport) => {
  try {
    const exportItem = await methodPromise('videos.exportAnnotations', videoOps.getId(video));
    if (isSyncGPS) {
      const newAnnotations = map(exportItem.annotations, element => {
        const item = element;
        const newTimeGPS = videoCalculate.calculateTimeGPSAnnotation(video, item);
        item.timeGPS = newTimeGPS;
        return item;
      });
      const exportItemGPS = { annotations: newAnnotations };
      exportTextToLocalFile(
        `${videoOps.getName(video)}_GPS_annotations.json`,
        JSON.stringify(exportItemGPS, null, '  ')
      );
    } else {
      exportTextToLocalFile(
        `${videoOps.getName(video)}_annotations.json`,
        JSON.stringify(exportItem, null, '  ')
      );
    }
    handleCloseModalExport();
  } catch (error) {
    handleErrorInClient(error);
  }
};

const handleExportAll = async video => {
  try {
    const exportItem = await methodPromise('videos.exportAnnotations', videoOps.getId(video));
    const nameFile = `${videoOps.getName(video)}_complete.json`;
    exportItem.video = video;
    exportTextToLocalFile(nameFile, JSON.stringify(exportItem, null, '  '));
  } catch (error) {
    handleErrorInClient(error);
  }
};

const handleImport = async (file, videoId, spatialSync, syncTimeGPS = false, annotations = []) => {
  try {
    await importFromAnnotationsFile({
      file,
      videoId,
      spatialSync,
      syncTimeGPS,
      annotationsProps: annotations
    });
  } catch (error) {
    handleErrorInClient(error);
  }
};

const handleRemove = async (videoId, history, projectId) => {
  try {
    await methodPromise('videos.softDelete', videoId);
    Bert.alert('Video deleted!', 'warning');
    history.push(`/projects/${projectId}`);
  } catch (error) {
    handleErrorInClient(error);
  }
};

const toggleShared = async (videoId, isShared, audience) => {
  try {
    await methodPromise('videos.toggleShared', videoId, !isShared, audience);
    if (!isShared) {
      Bert.alert('Link Created', 'success');
    } else {
      Bert.alert('No longer Shared', 'info');
    }
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

const ShareButton = ({ video, isFree }) => {
  const audienceVideo = videoOps.getAudience(video);
  let stringShared;
  if (audienceVideo === 'all') {
    stringShared = 'Unshare Public';
  } else if (audienceVideo === 'limited') {
    stringShared = 'Unshare Unlisted';
  } else if (audienceVideo === 'private') {
    stringShared = 'Unshare Private';
  } else if (audienceVideo === 'privateLimited') {
    stringShared = 'Unshare Unlisted Private';
  }

  if (!videoOps.isShared(video)) {
    return (
      <DropdownButton
        as={ButtonGroup}
        size="sm"
        variant="outline-secondary"
        title="Share"
        id="shareButton"
      >
        <Dropdown.Item
          id="public"
          eventKey={3}
          onClick={() => toggleShared(video._id, videoOps.isShared(video), ['all'])}
        >
          Public
        </Dropdown.Item>

        {!isFree && (
          <>
            <Dropdown.Item
              id="unlisted"
              eventKey={4}
              onClick={() => toggleShared(video._id, videoOps.isShared(video), ['limited'])}
            >
              Unlisted
            </Dropdown.Item>
            <Dropdown.Item
              id="private"
              eventKey={5}
              onClick={() => toggleShared(video._id, videoOps.isShared(video), ['private'])}
            >
              Private
            </Dropdown.Item>
            <Dropdown.Item
              id="privateLimited"
              eventKey={6}
              onClick={() => toggleShared(video._id, videoOps.isShared(video), ['privateLimited'])}
            >
              Private Unlisted
            </Dropdown.Item>
          </>
        )}
      </DropdownButton>
    );
  }
  return (
    <Button
      key={2}
      variant="outline-secondary"
      size="sm"
      onClick={() => toggleShared(video._id, videoOps.isShared(video), ['limited'])}
    >
      {stringShared}
    </Button>
  );
};

ShareButton.defaultProps = {
  video: {}
};

ShareButton.propTypes = {
  video: PropTypes.object,
  isFree: PropTypes.bool.isRequired
};

const ViewVideo = ({ isPremium, isFree, history, loading, match, project, video, ...rest }) => {
  const { annotations } = rest;
  let isPrivate = false;
  const audienceVideo = videoOps.getAudience(video);
  if (audienceVideo === 'private' || audienceVideo === 'privateLimited') {
    isPrivate = true;
  } else isPrivate = false;
  const embedURL = videoOps.getURL(video).substring(17, videoOps.getURL(video).length);
  const embedImageURL = `https://i.ytimg.com/vi/${embedURL}/0.jpg`;

  if (loading) return <Loading />;
  if (videoOps.isDeleted(video) || projectOps.isDeleted(project)) return <NotFound />;

  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState({});
  const [openModalExport, setOpenModalExport] = useState(false);

  const openModal = selectedFile => {
    setFile(selectedFile);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleModal = async (spatialSync, syncTimeGPS = false) => {
    await handleImport(file, videoOps.getId(video), spatialSync, syncTimeGPS, annotations);
    setFile({});
    closeModal();
  };
  const handleCloseModalExport = () => setOpenModalExport(false);
  return (
    <Row>
      <Col>
        <SEO
          title={videoOps.getName(video)}
          description={videoOps.getDescription(video)}
          images={{ twitter: embedImageURL }}
          path={`projects/${videoOps.getProject(video)}/videos/${video._id}`}
          contentType="video"
          published={videoOps.getCreation(video)}
          updated={videoOps.getLastUpdate(video)}
        />
        <PageHeader>
          <ConfirmationModalImport
            showModal={showModal}
            onCloseFunction={closeModal}
            onConfirmFunction={handleModal}
            video={video}
          />
          <h4>{videoOps.getName(video)}</h4>
          <div className="ml-auto d-flex flex-row align-items-center justify-content-end">
            <ShareIcon
              shared={videoOps.isShared(video)}
              shareUrl={
                isPrivate
                  ? `sharedPrivate/videos/${videoOps.getId(video)}`
                  : `shared/videos/${videoOps.getId(video)}`
              }
            />
            <CopyURLIcon
              shared={videoOps.isShared(video)}
              shareUrl={
                isPrivate
                  ? `sharedPrivate/videos/${videoOps.getId(video)}`
                  : `shared/videos/${videoOps.getId(video)}`
              }
            />

            <ButtonGroup size="sm">
              {!isFree && (
                <>
                  <ButtonImport
                    hasImportButton
                    isPremium={isPremium}
                    itemName="annotations"
                    smSize
                    variant="outline-secondary"
                    handleImport={selectedFile => openModal(selectedFile)}
                  />
                  <Button
                    id="export-annotations"
                    onClick={() => setOpenModalExport(true)}
                    variant="outline-secondary"
                  >
                    <FontAwesomeIcon icon="upload" /> Export Annotations
                  </Button>
                  <Button
                    id="export-all"
                    variant="outline-secondary"
                    onClick={() => handleExportAll(video)}
                  >
                    <FontAwesomeIcon icon="upload" /> Export All
                  </Button>
                </>
              )}
              <ShareButton video={video} isFree={isFree} />
              <Button
                variant="outline-info"
                id="edit-video"
                onClick={() => history.push(`${match.url}/edit`)}
              >
                Edit
              </Button>
              <Button
                id="soft-delete-video"
                onClick={() => handleRemove(video._id, history, match.params.project_id)}
                variant="outline-danger"
              >
                Delete
              </Button>
            </ButtonGroup>
          </div>
        </PageHeader>
        <VideoVisualizerContainer video={video} {...rest} isFree={isFree} isPublic={false} />
      </Col>
      <Modal show={openModalExport} onHide={handleCloseModalExport} justifyContent="center">
        <Modal.Header closeButton>
          <Modal.Title>Export annotations</Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => handleExport(false, video, handleCloseModalExport)}
          >
            Export without GPS time
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleExport(true, video, handleCloseModalExport)}
          >
            Export with GPS time
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

ViewVideo.defaultProps = {
  annotations: [],
  project: {},
  video: {},
  isFree: false
};

ViewVideo.propTypes = {
  annotations: PropTypes.array,
  history: PropTypes.object.isRequired,
  isPremium: PropTypes.bool.isRequired,
  isFree: PropTypes.bool,
  loading: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  project: PropTypes.object,
  video: PropTypes.object
};

export default compose(
  connect(state => ({ ...state })),
  withTracker(({ match }) => {
    const videoId = match.params.video_id;
    const videoSub = Meteor.isClient && Meteor.subscribe('videos.view', videoId);
    const video = Videos.findOne(videoId);
    const annotationsSub = Meteor.isClient && Meteor.subscribe('annotations.video', videoId);
    const annotations = Annotations.find({}, { sort: { videoTime: 1 } }).fetch();
    const projectId = match.params.project_id;
    const projectSub = Meteor.isClient && Meteor.subscribe('projects.video', videoId);
    const project = Projects.findOne(projectId);

    return {
      loading:
        !videoSub ||
        !videoSub.ready() ||
        !projectSub ||
        !projectSub.ready() ||
        !annotationsSub ||
        !annotationsSub.ready(),
      video,
      project,
      annotations
    };
  })
)(ViewVideo);
